// CF-HEADER-HELP-MENU-V1 — shared-header "⋯" overflow menu.
//
// One discoverable spot (top-right of the app shell, beside the wallet chip)
// that surfaces three things which were previously buried in tiny grey footers
// or had no in-app home at all:
//   • Send feedback   → App switches to the existing Feedback screen
//   • Terms          → opens the public /terms page (shared App.openTerms)
//   • Privacy        → opens the public /privacy page (shared App.openPrivacy)
//   • Support         → the variant SUPPORT_EMAIL (copy + toast; best-effort mailto)
//
// ADDITIVE: the Flip-screen footer (now separate Terms + Privacy links per
// CF-PRIVACY-PAGE-V1) and the Activity "Found a bug or have feedback?" footer
// both stay; this ⋯ menu surfaces the same actions in one discoverable spot.
//
// No silent fallbacks (PL #7178): the Support action ALWAYS surfaces the
// address via a toast — copied when the clipboard API is available, shown
// verbatim to type when it is not — and the mailto open is strictly a
// best-effort secondary path, never the only way to reach the address.

import { useState, useEffect, useRef } from 'react';
import { useT } from '../i18n';

// CF-GERMAN-MUENZO-REBRAND-V1: the support address is now variant-aware (german
// uses the Münzo inbox, never the cricketflip.com one). Defined centrally beside
// BRAND_NAME in lib/app-variant.ts and re-exported here so the existing public
// surface (the :67 clipboard, :75 toast, :80 mailto, :154 JSX refs) is unchanged.
export { SUPPORT_EMAIL } from '../lib/app-variant';
import { SUPPORT_EMAIL } from '../lib/app-variant';

interface HeaderMenuProps {
    onHowItWorks: () => void;
    onFeedback: () => void;
    onTerms: () => void;
    onPrivacy: () => void;
}

export function HeaderMenu({ onHowItWorks, onFeedback, onTerms, onPrivacy }: HeaderMenuProps) {
    const t = useT();
    const [open, setOpen] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const toastTimer = useRef<number | null>(null);

    const showToast = (msg: string) => {
        setToast(msg);
        if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
        toastTimer.current = window.setTimeout(() => setToast(null), 4000);
    };

    // Clear any pending toast timer on unmount so it can't fire into a gone tree.
    useEffect(() => () => {
        if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    }, []);

    // Esc closes the menu while it is open (desktop / hardware keyboard).
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const handleHowItWorks = () => { setOpen(false); onHowItWorks(); };
    const handleFeedback = () => { setOpen(false); onFeedback(); };
    const handleTerms = () => { setOpen(false); onTerms(); };
    const handlePrivacy = () => { setOpen(false); onPrivacy(); };

    const handleSupport = async () => {
        setOpen(false);
        // Reliable primary path: copy the address. `copied` records whether the
        // clipboard write actually succeeded so the toast can be honest about it.
        let copied = false;
        try {
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                await navigator.clipboard.writeText(SUPPORT_EMAIL);
                copied = true;
            }
        } catch {
            copied = false;
        }
        // Never silent: the toast surfaces the address in BOTH branches — copied,
        // or shown verbatim to type when the clipboard API is missing/denied.
        showToast(copied ? t('header_menu.support.copied_toast', { SUPPORT_EMAIL }) : SUPPORT_EMAIL);
        // Best-effort secondary: open the device mail composer. mailto is flaky
        // inside the Telegram in-app browser, so it is never the only path.
        try {
            const wa = (window as any).Telegram?.WebApp;
            const mailto = `mailto:${SUPPORT_EMAIL}`;
            if (wa && typeof wa.openLink === 'function') wa.openLink(mailto);
            else window.open(mailto, '_blank', 'noopener');
        } catch {
            /* copy + toast already gave the user the address — nothing to recover */
        }
    };

    return (
        <div className="relative">
            <button
                type="button"
                aria-label={t('header_menu.more_options_aria')}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen(prev => !prev)}
                className="flex items-center justify-center w-10 h-10 rounded-full transition-all active:scale-95"
                style={{ color: 'var(--page-text-secondary)' }}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="5" cy="12" r="1.7" />
                    <circle cx="12" cy="12" r="1.7" />
                    <circle cx="19" cy="12" r="1.7" />
                </svg>
            </button>

            {open && (
                <>
                    {/* Outside-tap overlay (transparent) — closes the menu. */}
                    <div className="fixed inset-0 z-[95]" aria-hidden="true" onClick={() => setOpen(false)} />
                    <div
                        role="menu"
                        aria-label={t('header_menu.more_options_aria')}
                        className="absolute right-0 top-full mt-2 w-60 rounded-2xl overflow-hidden z-[96] shadow-2xl"
                        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}
                    >
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handleHowItWorks}
                            className="w-full text-left px-4 py-3 text-[13px] font-semibold transition-colors active:opacity-70"
                            style={{ color: 'var(--text-secondary)' }}
                        >
                            {t('header_menu.how_it_works')}
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handleFeedback}
                            className="w-full text-left px-4 py-3 text-[13px] font-semibold transition-colors active:opacity-70"
                            style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border-subtle)' }}
                        >
                            {t('header_menu.send_feedback')}
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handleTerms}
                            className="w-full text-left px-4 py-3 text-[13px] font-semibold transition-colors active:opacity-70"
                            style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border-subtle)' }}
                        >
                            {t('header_menu.terms')}
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handlePrivacy}
                            className="w-full text-left px-4 py-3 text-[13px] font-semibold transition-colors active:opacity-70"
                            style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border-subtle)' }}
                        >
                            {t('header_menu.privacy')}
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handleSupport}
                            className="w-full text-left px-4 py-3 transition-colors active:opacity-70"
                            style={{ borderTop: '1px solid var(--border-subtle)' }}
                        >
                            <span className="block text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>
                                {t('header_menu.support')}
                            </span>
                            <span className="block text-[11px] font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                {SUPPORT_EMAIL}
                            </span>
                        </button>
                    </div>
                </>
            )}

            {toast && (
                <div
                    role="status"
                    aria-live="polite"
                    className="fixed left-1/2 -translate-x-1/2 z-[120] px-4 py-2.5 rounded-xl text-[12px] font-semibold pointer-events-none whitespace-nowrap"
                    style={{
                        top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                    }}
                >
                    {toast}
                </div>
            )}
        </div>
    );
}
